import { G } from '@mobily/ts-belt';
import { z } from 'zod';

import { MAX_CTO_CUSTODY_FEE_PERCENTAGE_RATE } from '@Common/constants/fees';
import { ZodInvestingExperienceQuestionnaireV2 } from '@Common/types/bo_wm/customers/investing_experience_questionnaire_v2';
import { zodDecimalAmount5 } from '@Common/types/currency/currency';
import { ZodEmploymentStatus } from '@Common/types/Employment';
import { ZodFundingSource } from '@Common/types/Funding';
import { ZodEmployerIndustryTypeV3 } from '@Common/types/questions/employer_industry';
import { customNationalityValidation, ZodAddressInfo } from '@Common/types/questions/user_profile';
import { ZodCity } from '@Common/types/user/address';
import { ZodUserBirthdate } from '@Common/types/user/birthdate';
import { ZodUserFirstName, ZodUserLastName } from '@Common/types/user/legal_name';
import { ZodSEPABankAccount } from '@Common/types/zod/bank_account';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { ZodAppUserEmail } from '@Common/types/zod/zod-email';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodPhoneNumber } from '@Common/types/zod/zod-phone-number';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodWMCustodyFee = z
  .number()
  .min(0)
  .max(MAX_CTO_CUSTODY_FEE_PERCENTAGE_RATE)
  .multipleOf(0.001);

export const ZodWMCustomerPepStatus = z.enum([
  'is_politically_exposed',
  'is_politically_exposed_relative',
]);

export const UserInvestmentHorizonValues = [
  'up_to_1_year',
  '1_3_years',
  '3_5_years',
  '5_8_years',
  'more_than_8_years',
] as const;

export const ZodUserInvestmentHorizon = z.enum(UserInvestmentHorizonValues);

export type UserInvestmentHorizonType = z.infer<typeof ZodUserInvestmentHorizon>;

export const UserInvestmentObjectiveValues = [
  'grow_wealth',
  'additional_income',
  'optimize_taxation',
  'future_project',
  'retirement',
  'transfer_capital',
] as const;

const ZodUserInvestmentObjective = z.enum(UserInvestmentObjectiveValues);

export type UserInvestmentObjectiveType = z.infer<typeof ZodUserInvestmentObjective>;

export const ZodPersonalUserInvestmentObjectives = z.array(ZodUserInvestmentObjective);

const CustomerCountryCodesIFUCityRequired = ['FR', 'NC', 'PF'] as const;
type CustomerCountryCodesIFUCityRequiredType = (typeof CustomerCountryCodesIFUCityRequired)[number];

// Zod forbids `.pick()`/`.omit()`/`.partial()` on refined objects, so derive from this one
export const ZodWMCustomerIFUDataUnrefined = z.object({
  country_of_birth: ZodCountryCode,
  place_of_birth: ZodCity.nullable(),
  postcode_of_birth: ZodTrimmedString.nullable(),
});

export const ZodWMCustomerIFUData = ZodWMCustomerIFUDataUnrefined.refine(
  (data) =>
    !CustomerCountryCodesIFUCityRequired.includes(
      data.country_of_birth as CustomerCountryCodesIFUCityRequiredType,
    ) ||
    (G.isNotNullable(data.place_of_birth) && G.isNotNullable(data.postcode_of_birth)),
  {
    message: 'Place of birth and postcode of birth are required for given country of birth',
  },
);

export type WMCustomerIFUData = z.infer<typeof ZodWMCustomerIFUData>;

const ZodWMCustomerCountryCode = z.enum([...CustomerCountryCodesIFUCityRequired, 'BL', 'MF']);

export type WMCustomerCountryCode = z.infer<typeof ZodWMCustomerCountryCode>;

export const ZodCreateCustomerBody = z
  .object({
    firstname: ZodUserFirstName,
    lastname: ZodUserLastName,
    gender: z.enum(['male', 'female']).nullish(),
    birthdate: ZodUserBirthdate,
    phone_number: ZodPhoneNumber,
    email: ZodAppUserEmail,
    nationality: ZodCountryCode,
    other_nationality: ZodCountryCode.nullish(),
    national_identifier: ZodTrimmedString.nullish(),
    tax_number: ZodTrimmedString.nullish(),
    country_of_tax_residence: ZodWMCustomerCountryCode,
    pep_status: ZodWMCustomerPepStatus.nullish(),
    home_address: ZodAddressInfo,
    financial: z.object({
      funding_source: ZodFundingSource,
      employment_status: ZodEmploymentStatus,
      employer_industry: ZodEmployerIndustryTypeV3.optional(),
      job_title: ZodTrimmedString.optional(),
      annual_index: z.number().int(),
      invest_index: z.number().int(),
      estimated_net_worth_index: z.number().int(),
      bank_account: ZodSEPABankAccount.optional(),
      investment: z.object({
        horizon: ZodUserInvestmentHorizon,
        objectives: ZodPersonalUserInvestmentObjectives.min(1),
      }),
    }),
    investing_experience_questionnaire_v2: ZodInvestingExperienceQuestionnaireV2,
    custody_fee: ZodWMCustodyFee.optional(),
    distributor_fee_rate: zodDecimalAmount5
      .and(z.number().min(0).max(0.004).multipleOf(0.00001))
      .optional(),
    ifu: ZodWMCustomerIFUData.optional(),
    draft_id: z.number().optional(),
  })
  .superRefine(customNationalityValidation);

export type CreateCustomerPayload = z.infer<typeof ZodCreateCustomerBody>;

export const ZodCreateCustomerResponse = z.object({
  id: z.number(),
  display_name: ZodTrimmedString,
});

export type CreateCustomerResponse = z.infer<typeof ZodCreateCustomerResponse>;

export const postCreateCustomerEndpoint = endpoint({
  method: 'POST',
  body: ZodCreateCustomerBody,
  responseBody: ZodCreateCustomerResponse,
});

export type PostCreateCustomerEndpoint = GetEndpointType<typeof postCreateCustomerEndpoint>;
